// The claims on one job or on an invoice's jobs (finance-real, the user 2026-10-23: "pressing on this allows me to change
// the claim", "here I can add a new claim"), one list the job drawer and the invoice drawer share. A saved row carries no
// button (DD-045): pressing it opens it in place as the claim form (ClaimForm: its person, its amount with the share of
// the price and what is free, Save and Take off), headed with whose claim on which job ("name · period", jobLabel) where
// the list names several jobs; a claim on a received or paid team invoice shows its lock and does not open. A team
// invoice's claims open the same form in place (TeamInvoiceDrawer). Add a claim opens the same form: the job
// (on an invoice, those it can still claim on), who, the amount. Escape or Cancel closes the form alone and gives focus
// back to its row, or to Add a claim. It displays what the drawer's hook reads and calls the writes it hands over, so it
// has no hook of its own (TD-107).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { PlusIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import type { UseMutationResult } from "@tanstack/react-query";
import { ClaimForm, type ClaimJob } from "../components/ClaimForm";
import type { JobClaim, JobClaimInput, LineJob } from "../types";
import { jobLabel, NO_CLAIM } from "../utils/names";

const CLAIM = ENTITY_CONFIG.claim;

type AddClaim = UseMutationResult<JobClaim, Error, { jobId: string; input: JobClaimInput }>;
type UpdateClaim = UseMutationResult<JobClaim, Error, { claimId: string; input: JobClaimInput }>;
type RemoveClaim = UseMutationResult<JobClaim, Error, string>;

interface Props {
  claims: JobClaim[];
  loading?: boolean;
  /** The jobs the claims are on: a claim's cap and its job's name. */
  jobs: LineJob[];
  /** Each row names its job after the person, and Add asks which job (an invoice's several jobs). */
  showJob?: boolean;
  people: { id: string; name: string }[];
  add: AddClaim;
  update: UpdateClaim;
  remove: RemoveClaim;
  /** The section's bordered total row under the rows: "Claimed", the server's figure. */
  total?: { label: string; amountMinor: number };
  /** Between the rows and Add a claim: the job drawer's free to claim. */
  note?: ReactNode;
}

/** It takes a claim now: the one claim rule (claimable), with room under its cap. */
export const takesClaim = (job: LineJob): boolean => job.claimable && job.freeMinor > 0;

/** A job as the claim form reads it: the server's figures (TD-102). */
export const claimJobOf = (j: LineJob): ClaimJob => ({ id: j.id, label: jobLabel(j.name, j.periodLabel), priceMinor: j.priceMinor, marginPct: j.marginPct, capMinor: j.capMinor, freeMinor: j.freeMinor });

const ADD = "add";

export function ClaimsList({ claims, loading = false, jobs, showJob = false, people, add, update, remove, total, note }: Props) {
  const [opened, setOpened] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // Where focus goes back once a form closes: its row, or Add a claim.
  const back = useRef<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const jobOf = (id: string): LineJob | undefined => jobs.find((j) => j.id === id);
  const addable = jobs.filter(takesClaim);
  useEffect(() => {
    if (opened !== null || adding || back.current === null) return;
    list.current?.querySelector<HTMLElement>(`[data-claim-focus="${back.current}"]`)?.focus();
    back.current = null;
  }, [opened, adding]);
  const open = (id: string) => {
    update.reset();
    remove.reset();
    setAdding(false);
    setOpened(id);
  };
  const shut = (to: string) => {
    back.current = to;
    setOpened(null);
    setAdding(false);
  };
  const startAdding = () => {
    add.reset();
    setOpened(null);
    setAdding(true);
  };
  return (
    <div ref={list} className="space-y-2">
      {loading ? null : claims.length === 0 ? (
        <p className="text-sm text-gray-400">{NO_CLAIM}</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {claims.map((c) => {
            const job = jobOf(c.jobId);
            const cells = (
              <>
                {/* DD-055: on a phone the name and job wrap, the amount stays in view on the right. */}
                <span className="min-w-0 text-gray-900 [overflow-wrap:anywhere]">
                  {c.name}
                  {showJob && job && <span className="text-gray-500">{` · ${jobLabel(job.name, job.periodLabel)}`}</span>}
                  {c.lock && <span className="block text-xs text-gray-500">{c.lock}</span>}
                </span>
                <MoneyText amountMinor={c.amountMinor} className="text-sm shrink-0" />
              </>
            );
            // A claim on a received or paid team invoice is held, and a dropped job's no longer changes: neither opens.
            const opens = c.lock === null && job !== undefined && job.status !== "dropped" && job.pot === null;
            return (
              <li key={c.id}>
                {opened === c.id && job ? (
                  <div className="my-1">
                    <ClaimForm
                      job={claimJobOf(job)}
                      people={people}
                      saved={{ personId: c.personId, amountMinor: c.amountMinor, maxMinor: c.maxMinor, overCapMinor: c.overCapMinor }}
                      heading={showJob ? `${c.name} · ${jobLabel(job.name, job.periodLabel)}` : undefined}
                      verb="Save"
                      canSubmit={job.claimable}
                      note={job.claimable ? undefined : `${jobLabel(job.name, job.periodLabel)} takes no claim now: the claim can only be taken off.`}
                      pending={update.isPending}
                      removing={remove.isPending}
                      refused={update.error?.message ?? remove.error?.message}
                      onSubmit={(d) => update.mutate({ claimId: c.id, input: { personId: d.personId ?? c.personId, amountMinor: d.amountMinor } }, { onSuccess: () => shut(c.id) })}
                      onRemove={() => remove.mutate(c.id, { onSuccess: () => shut(ADD) })}
                      onCancel={() => shut(c.id)}
                    />
                  </div>
                ) : opens ? (
                  <button
                    type="button"
                    data-claim-focus={c.id}
                    onClick={() => open(c.id)}
                    aria-label={`Change ${c.name}'s claim`}
                    className="flex w-full items-center justify-between gap-3 py-1.5 text-sm text-left hover:bg-gray-50"
                  >
                    {cells}
                  </button>
                ) : (
                  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">{cells}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {total && (
        <div className="flex items-center justify-between gap-3 pt-2 mt-1 border-t border-gray-200">
          <span className="text-sm font-medium text-gray-900">{total.label}</span>
          <MoneyText amountMinor={total.amountMinor} strong className="text-sm" />
        </div>
      )}
      {note}
      {addable.length > 0 &&
        (adding ? (
          <ClaimForm
            job={showJob ? undefined : claimJobOf(addable[0])}
            jobs={addable.map(claimJobOf)}
            people={people}
            verb="Add the claim"
            pending={add.isPending}
            refused={add.error?.message}
            onSubmit={(d) => d.personId && add.mutate({ jobId: d.jobId, input: { personId: d.personId, amountMinor: d.amountMinor } }, { onSuccess: () => shut(ADD) })}
            onCancel={() => shut(ADD)}
          />
        ) : (
          <Button size="sm" variant="secondary" data-claim-focus={ADD} onClick={startAdding}>
            <PlusIcon className="w-4 h-4 mr-1" />
            {CLAIM.createLabel}
          </Button>
        ))}
    </div>
  );
}
